import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  registerManagedAgent,
  registerAgentSelfFund,
  initOwnerPaidAgent,
  completeOwnerPaidAgent,
  fetchOwnerAgents,
  fetchDiscoverAgents,
  fetchOwnedAgentDetail,
  updateOwnedAgent,
  addOwnedAgentEndpoint,
  addOwnedAgentWallet,
  setPrimaryOwnedAgentWallet,
  deleteOwnedAgentWallet,
  AddAgentWalletRejection,
  updateOwnedAgentEndpoint,
  deleteOwnedAgentEndpoint,
  RegisteredAgent,
  RegisterManagedAgentRejection,
  RegisterAgentSelfFundRejection,
  CompleteOwnerPaidRejection,
  UpdateAgentRejection,
  AddEndpointRejection,
  InitOwnerPaidResponse,
  OwnedAgent,
  OwnedAgentDetail,
  DiscoverableAgent,
  fetchAgentReceipts,
  AgentReceiptDto,
  addOwnedAgentAcceptedSettlement,
  deleteOwnedAgentAcceptedSettlement,
} from '../thunks/agentThunks';

/**
 * Agent-registration UI state (ADR-011 Phase 2b).
 *
 * The three flows (subsidy / owner-paid email / owner-paid wallet)
 * share one `status` field for "am I submitting anything right now"
 * and one `rejection` field for the last failure. Flow-specific
 * details (pendingAgent from init, insufficientFunds from self-fund)
 * live in dedicated slots so the page can render each surface without
 * a discriminated payload.
 */

export type RegisterStatus = 'idle' | 'submitting' | 'success' | 'rejected';

export type AgentRejection =
  | RegisterManagedAgentRejection
  | RegisterAgentSelfFundRejection
  | CompleteOwnerPaidRejection;

interface AgentState {
  status: RegisterStatus;
  lastAgent: RegisteredAgent | null;
  lastTxHash: string | null;
  rejection: AgentRejection | null;

  // Set when /register/init succeeds. Wallet-user owner-paid flow reads
  // this to know which address to fund + how much KLV to send, then
  // passes agentUserId back on /register/complete.
  pendingAgent: {
    agentUserId: string;
    agentWalletAddress: string;
    requiredKlvSmallest: string;
    requiredKlvDisplay: string;
  } | null;

  // Set when /register/self-fund fails with 402. Page renders the
  // required-vs-available amounts + a link to top up.
  insufficientFunds: {
    requiredKlv: string;
    availableKlv: string;
  } | null;

  // "My agents" listing state (Ring 1). Kept separate from the
  // registration state so a re-fetch during registration doesn't
  // clobber the in-flight submit's error banner.
  ownedAgents: OwnedAgent[];
  ownedAgentsStatus: 'idle' | 'loading' | 'loaded' | 'rejected';
  ownedAgentsError: string | null;

  // Public discover listing state (Ring 2). Cursor pagination — when
  // `discoverNextCursor` is null the current list is the last page.
  // Append behavior is driven by the thunk's appendedToPrevious flag.
  discoverAgents: DiscoverableAgent[];
  discoverStatus: 'idle' | 'loading' | 'loading_more' | 'loaded' | 'rejected';
  discoverError: string | null;
  discoverNextCursor: string | null;
  discoverAppliedCapabilities: string[];
  discoverAppliedChains: string[];
  /** Total agents matching the current filter (capability + chain).
   *  Backend sends this only on first-page loads; we cache the value
   *  across Load-more calls until the filter changes. Null before
   *  the first response lands. Used by the "Showing N of TOTAL"
   *  indicator on the discover page. */
  discoverTotal: number | null;
  /** Currently-active global-scope agent spotlights. Backend sends
   *  this only on first-page loads; cache across Load-more calls.
   *  Empty array = no active spotlights (section hidden). Used by
   *  the "Spotlighted" rail at the top of /agents/discover. */
  discoverSpotlights: DiscoverableAgent[];

  // Ring 2.5 — edit surface state. detail is the currently-loaded
  // agent for the edit page. saveStatus tracks the in-flight PATCH
  // or endpoint add/delete so the button can show a spinner + gate
  // duplicate submissions.
  editDetail: OwnedAgentDetail | null;
  editLoadStatus: 'idle' | 'loading' | 'loaded' | 'rejected';
  editLoadError: string | null;
  editSaveStatus: 'idle' | 'saving' | 'saved' | 'rejected';
  editSaveRejection: UpdateAgentRejection | null;
  endpointSaveStatus: 'idle' | 'saving' | 'saved' | 'rejected';
  endpointSaveRejection: AddEndpointRejection | null;

  // Wallet CRUD status. Shared across the three wallet operations
  // (add / set-primary / delete) since the edit-page card only shows
  // one spinner at a time. Rejection carries the add-flow's tagged
  // union so the form can surface duplicate-address inline.
  walletSaveStatus: 'idle' | 'saving' | 'saved' | 'rejected';
  walletSaveRejection: AddAgentWalletRejection | null;

  // Ring 3 Phase 1 receipt listing state, keyed by provider agent
  // user id so more than one agent's receipts can coexist in the
  // store (e.g. tabbing between two agent profiles). Each entry
  // holds the paginated list + cursor + status.
  receiptsByAgent: Record<
    string,
    {
      receipts: AgentReceiptDto[];
      status: 'idle' | 'loading' | 'loading_more' | 'loaded' | 'rejected';
      error: string | null;
      nextCursor: string | null;
    }
  >;
}

const initialState: AgentState = {
  status: 'idle',
  lastAgent: null,
  lastTxHash: null,
  rejection: null,
  pendingAgent: null,
  insufficientFunds: null,
  ownedAgents: [],
  ownedAgentsStatus: 'idle',
  ownedAgentsError: null,
  discoverAgents: [],
  discoverTotal: null,
  discoverSpotlights: [],
  discoverStatus: 'idle',
  discoverError: null,
  discoverNextCursor: null,
  discoverAppliedCapabilities: [],
  discoverAppliedChains: [],
  editDetail: null,
  editLoadStatus: 'idle',
  editLoadError: null,
  editSaveStatus: 'idle',
  editSaveRejection: null,
  endpointSaveStatus: 'idle',
  endpointSaveRejection: null,
  walletSaveStatus: 'idle',
  walletSaveRejection: null,
  receiptsByAgent: {},
};

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    resetRegistration(state) {
      state.status = 'idle';
      state.rejection = null;
      state.pendingAgent = null;
      state.insufficientFunds = null;
      // Keep lastAgent + lastTxHash — the confirmation surface can
      // still render after a form reset.
    },
    resetAgentEdit(state) {
      state.editDetail = null;
      state.editLoadStatus = 'idle';
      state.editLoadError = null;
      state.editSaveStatus = 'idle';
      state.editSaveRejection = null;
      state.endpointSaveStatus = 'idle';
      state.endpointSaveRejection = null;
      state.walletSaveStatus = 'idle';
      state.walletSaveRejection = null;
    },
  },
  extraReducers: (builder) => {
    // --- subsidy path ---
    builder
      .addCase(registerManagedAgent.pending, (state) => {
        state.status = 'submitting';
        state.rejection = null;
        state.insufficientFunds = null;
      })
      .addCase(registerManagedAgent.fulfilled, (state, action) => {
        state.status = 'success';
        state.lastAgent = action.payload.agent;
        state.lastTxHash = action.payload.txHash;
        state.rejection = null;
      })
      .addCase(registerManagedAgent.rejected, (state, action) => {
        state.status = 'rejected';
        state.rejection = action.payload ?? { kind: 'error', message: 'Failed to register agent' };
      });

    // --- owner-paid one-shot (email) ---
    builder
      .addCase(registerAgentSelfFund.pending, (state) => {
        state.status = 'submitting';
        state.rejection = null;
        state.insufficientFunds = null;
      })
      .addCase(registerAgentSelfFund.fulfilled, (state, action) => {
        state.status = 'success';
        state.lastAgent = action.payload.agent;
        state.lastTxHash = action.payload.txHash;
        state.rejection = null;
        state.insufficientFunds = null;
      })
      .addCase(registerAgentSelfFund.rejected, (state, action) => {
        state.status = 'rejected';
        const r = action.payload ?? { kind: 'error' as const, message: 'Failed to register agent' };
        state.rejection = r;
        if (r.kind === 'insufficient_funds') {
          state.insufficientFunds = {
            requiredKlv: r.requiredKlv,
            availableKlv: r.availableKlv,
          };
        }
      });

    // --- owner-paid two-step (wallet) ---
    builder
      .addCase(initOwnerPaidAgent.pending, (state) => {
        state.status = 'submitting';
        state.rejection = null;
      })
      .addCase(initOwnerPaidAgent.fulfilled, (state, action: PayloadAction<InitOwnerPaidResponse>) => {
        state.status = 'idle';
        state.pendingAgent = {
          agentUserId: action.payload.agentUserId,
          agentWalletAddress: action.payload.agentWalletAddress,
          requiredKlvSmallest: action.payload.requiredKlv.smallest,
          requiredKlvDisplay: action.payload.requiredKlv.display,
        };
        state.rejection = null;
      })
      .addCase(initOwnerPaidAgent.rejected, (state, action) => {
        state.status = 'rejected';
        state.rejection = action.payload ?? { kind: 'error', message: 'Failed to initialize agent registration' };
      });

    builder
      .addCase(completeOwnerPaidAgent.pending, (state) => {
        state.status = 'submitting';
        state.rejection = null;
      })
      .addCase(completeOwnerPaidAgent.fulfilled, (state, action) => {
        state.status = 'success';
        state.lastAgent = action.payload.agent;
        state.lastTxHash = action.payload.txHash;
        state.rejection = null;
        state.pendingAgent = null;
      })
      .addCase(completeOwnerPaidAgent.rejected, (state, action) => {
        state.status = 'rejected';
        state.rejection = action.payload ?? { kind: 'error', message: 'Failed to complete agent registration' };
      });

    // --- "My agents" listing (Ring 1) ---
    builder
      .addCase(fetchOwnerAgents.pending, (state) => {
        state.ownedAgentsStatus = 'loading';
        state.ownedAgentsError = null;
      })
      .addCase(fetchOwnerAgents.fulfilled, (state, action) => {
        state.ownedAgentsStatus = 'loaded';
        state.ownedAgents = action.payload.agents;
        state.ownedAgentsError = null;
      })
      .addCase(fetchOwnerAgents.rejected, (state, action) => {
        state.ownedAgentsStatus = 'rejected';
        state.ownedAgentsError =
          action.payload?.message ?? 'Failed to load your agents';
      });

    // --- Discover listing (Ring 2) ---
    builder
      .addCase(fetchDiscoverAgents.pending, (state, action) => {
        // Distinguish first-load (spinner takeover) from load-more
        // (append with a small spinner near the button) so the UI
        // doesn't blank the whole grid on pagination.
        state.discoverStatus = action.meta.arg.cursor ? 'loading_more' : 'loading';
        state.discoverError = null;
        // Track what filter this fetch was for so the reducer knows
        // which capability set the response corresponds to.
        if (!action.meta.arg.cursor) {
          state.discoverAppliedCapabilities = action.meta.arg.capabilities ?? [];
          state.discoverAppliedChains = action.meta.arg.chains ?? [];
        }
      })
      .addCase(fetchDiscoverAgents.fulfilled, (state, action) => {
        state.discoverStatus = 'loaded';
        state.discoverError = null;
        state.discoverNextCursor = action.payload.nextCursor;
        if (action.payload.appendedToPrevious) {
          state.discoverAgents = [
            ...state.discoverAgents,
            ...action.payload.agents,
          ];
          // Load-more responses don't carry `total` — keep the
          // cached value from the first-page fetch.
        } else {
          state.discoverAgents = action.payload.agents;
          // First page — server always includes total for this call.
          // null-coalesce in case the server hasn't been deployed yet.
          state.discoverTotal = action.payload.total ?? null;
          // Spotlights also first-page-only; empty array if none
          // active or if server pre-dates the spotlight wiring.
          state.discoverSpotlights = action.payload.spotlights ?? [];
        }
      })
      .addCase(fetchDiscoverAgents.rejected, (state, action) => {
        state.discoverStatus = 'rejected';
        state.discoverError =
          action.payload?.message ?? 'Failed to load agents';
      });

    // --- Ring 2.5 edit surface ---
    builder
      .addCase(fetchOwnedAgentDetail.pending, (state) => {
        state.editLoadStatus = 'loading';
        state.editLoadError = null;
        state.editSaveStatus = 'idle';
        state.editSaveRejection = null;
        state.endpointSaveStatus = 'idle';
        state.endpointSaveRejection = null;
      })
      .addCase(fetchOwnedAgentDetail.fulfilled, (state, action) => {
        state.editLoadStatus = 'loaded';
        state.editDetail = action.payload;
      })
      .addCase(fetchOwnedAgentDetail.rejected, (state, action) => {
        state.editLoadStatus = 'rejected';
        state.editLoadError = action.payload?.message ?? 'Failed to load agent';
      });

    builder
      .addCase(updateOwnedAgent.pending, (state) => {
        state.editSaveStatus = 'saving';
        state.editSaveRejection = null;
      })
      .addCase(updateOwnedAgent.fulfilled, (state, action) => {
        state.editSaveStatus = 'saved';
        state.editDetail = action.payload;
      })
      .addCase(updateOwnedAgent.rejected, (state, action) => {
        state.editSaveStatus = 'rejected';
        state.editSaveRejection = action.payload ?? {
          kind: 'error',
          message: 'Failed to update agent',
        };
      });

    builder
      .addCase(addOwnedAgentEndpoint.pending, (state) => {
        state.endpointSaveStatus = 'saving';
        state.endpointSaveRejection = null;
      })
      .addCase(addOwnedAgentEndpoint.fulfilled, (state, action) => {
        state.endpointSaveStatus = 'saved';
        // Append the new endpoint to the current editDetail in place
        // so the editor list updates without a re-fetch.
        if (state.editDetail && state.editDetail.agent.userId === action.payload.agentUserId) {
          state.editDetail = {
            ...state.editDetail,
            endpoints: [...state.editDetail.endpoints, action.payload.endpoint],
          };
        }
      })
      .addCase(addOwnedAgentEndpoint.rejected, (state, action) => {
        state.endpointSaveStatus = 'rejected';
        state.endpointSaveRejection = action.payload ?? {
          kind: 'error',
          message: 'Failed to add endpoint',
        };
      });

    // Ring 2.5 endpoint UPDATE — replace the matching row in place.
    builder
      .addCase(updateOwnedAgentEndpoint.fulfilled, (state, action) => {
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          state.editDetail = {
            ...state.editDetail,
            endpoints: state.editDetail.endpoints.map((e) =>
              e.id === action.payload.endpoint.id ? action.payload.endpoint : e,
            ),
          };
        }
      });

    builder
      .addCase(deleteOwnedAgentEndpoint.fulfilled, (state, action) => {
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          state.editDetail = {
            ...state.editDetail,
            endpoints: state.editDetail.endpoints.filter(
              (e) => e.id !== action.payload.endpointId,
            ),
          };
        }
      });

    // --- Per-chain wallets ---
    // Shared spinner + rejection across the three ops (add / set
    // primary / delete). Each fulfilled reducer mutates
    // editDetail.wallets in place; caller only sees a re-render once.
    builder
      .addCase(addOwnedAgentWallet.pending, (state) => {
        state.walletSaveStatus = 'saving';
        state.walletSaveRejection = null;
      })
      .addCase(addOwnedAgentWallet.fulfilled, (state, action) => {
        state.walletSaveStatus = 'saved';
        state.walletSaveRejection = null;
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          // If the server marked the new row primary (auto or
          // explicit), demote any other primary on the same chain in
          // our local copy so the UI matches what the DB now looks
          // like without a refetch.
          const next = action.payload.wallet;
          const existing = next.isPrimary
            ? state.editDetail.wallets.map((w) =>
                w.chain === next.chain && w.id !== next.id
                  ? { ...w, isPrimary: false }
                  : w,
              )
            : state.editDetail.wallets;
          state.editDetail = {
            ...state.editDetail,
            wallets: [...existing, next],
          };
        }
      })
      .addCase(addOwnedAgentWallet.rejected, (state, action) => {
        state.walletSaveStatus = 'rejected';
        state.walletSaveRejection = action.payload ?? null;
      });

    builder
      .addCase(setPrimaryOwnedAgentWallet.pending, (state) => {
        state.walletSaveStatus = 'saving';
        state.walletSaveRejection = null;
      })
      .addCase(setPrimaryOwnedAgentWallet.fulfilled, (state, action) => {
        state.walletSaveStatus = 'saved';
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          const promoted = action.payload.wallet;
          state.editDetail = {
            ...state.editDetail,
            // Any other row in the same chain gets demoted. The
            // promoted row itself takes the server's fresh state
            // (isPrimary: true).
            wallets: state.editDetail.wallets.map((w) => {
              if (w.id === promoted.id) return promoted;
              if (w.chain === promoted.chain) return { ...w, isPrimary: false };
              return w;
            }),
          };
        }
      })
      .addCase(setPrimaryOwnedAgentWallet.rejected, (state) => {
        state.walletSaveStatus = 'rejected';
      });

    builder
      .addCase(deleteOwnedAgentWallet.pending, (state) => {
        state.walletSaveStatus = 'saving';
        state.walletSaveRejection = null;
      })
      .addCase(deleteOwnedAgentWallet.fulfilled, (state, action) => {
        state.walletSaveStatus = 'saved';
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          state.editDetail = {
            ...state.editDetail,
            wallets: state.editDetail.wallets.filter(
              (w) => w.id !== action.payload.walletId,
            ),
          };
        }
      })
      .addCase(deleteOwnedAgentWallet.rejected, (state) => {
        state.walletSaveStatus = 'rejected';
      });

    // --- Accepted settlement lanes (issue #661) ---
    // Reuses the walletSaveStatus channel — same loading semantics
    // (fire-and-forget save, refetch not required because we mutate
    // editDetail in-place on fulfilled).
    builder
      .addCase(addOwnedAgentAcceptedSettlement.pending, (state) => {
        state.walletSaveStatus = 'saving';
        state.walletSaveRejection = null;
      })
      .addCase(addOwnedAgentAcceptedSettlement.fulfilled, (state, action) => {
        state.walletSaveStatus = 'saved';
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          const existing = state.editDetail.acceptedSettlements ?? [];
          // Idempotent add — replace an existing (asset, chain) row
          // (same tuple => same PK on the server) or append if new.
          const filtered = existing.filter(
            (l) =>
              !(l.asset === action.payload.lane.asset &&
                l.chain === action.payload.lane.chain),
          );
          state.editDetail = {
            ...state.editDetail,
            acceptedSettlements: [...filtered, action.payload.lane],
          };
        }
      })
      .addCase(addOwnedAgentAcceptedSettlement.rejected, (state, action) => {
        state.walletSaveStatus = 'rejected';
        state.walletSaveRejection = {
          kind: 'server',
          message: action.payload?.message ?? 'Failed to add accepted settlement',
        };
      })
      .addCase(deleteOwnedAgentAcceptedSettlement.pending, (state) => {
        state.walletSaveStatus = 'saving';
        state.walletSaveRejection = null;
      })
      .addCase(deleteOwnedAgentAcceptedSettlement.fulfilled, (state, action) => {
        state.walletSaveStatus = 'saved';
        if (
          state.editDetail &&
          state.editDetail.agent.userId === action.payload.agentUserId
        ) {
          state.editDetail = {
            ...state.editDetail,
            acceptedSettlements: (state.editDetail.acceptedSettlements ?? []).filter(
              (l) =>
                !(l.asset === action.payload.asset &&
                  l.chain === action.payload.chain),
            ),
          };
        }
      })
      .addCase(deleteOwnedAgentAcceptedSettlement.rejected, (state) => {
        state.walletSaveStatus = 'rejected';
      });

    // --- Ring 3 Phase 1 receipts (per-agent list) ---
    builder
      .addCase(fetchAgentReceipts.pending, (state, action) => {
        const key = action.meta.arg.providerAgentUserId;
        const existing = state.receiptsByAgent[key];
        // If we're paginating (cursor present) keep the current list
        // visible with a load_more spinner. First fetch flips to
        // loading + resets so a filter/reset shows a clean spinner.
        state.receiptsByAgent[key] = {
          receipts: action.meta.arg.cursor ? existing?.receipts ?? [] : [],
          status: action.meta.arg.cursor ? 'loading_more' : 'loading',
          error: null,
          nextCursor: existing?.nextCursor ?? null,
        };
      })
      .addCase(fetchAgentReceipts.fulfilled, (state, action) => {
        const key = action.meta.arg.providerAgentUserId;
        const existing = state.receiptsByAgent[key];
        const receipts = action.payload.appendedToPrevious
          ? [...(existing?.receipts ?? []), ...action.payload.receipts]
          : action.payload.receipts;
        state.receiptsByAgent[key] = {
          receipts,
          status: 'loaded',
          error: null,
          nextCursor: action.payload.nextCursor,
        };
      })
      .addCase(fetchAgentReceipts.rejected, (state, action) => {
        const key = action.meta.arg.providerAgentUserId;
        const existing = state.receiptsByAgent[key];
        state.receiptsByAgent[key] = {
          receipts: existing?.receipts ?? [],
          status: 'rejected',
          error: action.payload?.message ?? 'Failed to load receipts',
          nextCursor: existing?.nextCursor ?? null,
        };
      });
  },
});

export const { resetRegistration, resetAgentEdit } = agentSlice.actions;
export default agentSlice.reducer;
